import { useCallback, useEffect, useMemo, useState } from "react";

import { getErrorMessage } from "../../../api/errorMap";
import { useRBACPermission } from "../../../hooks/useRBACPermission";
import { useAuthStore } from "../../../stores/authStore";
import { MAX_PAGE_SIZE } from "../../../utils/formatters";
import { DataTable, type Column } from "../../components/DataTable";
import { showToast } from "../../components/ui/custom-toast";

import { deletePagScheduler, fetchPagSchedulers, fetchSourceSystemTools, savePagScheduler } from "./services/pagApi";
import { buildPgaSchedulerColumns } from "./helpers/pgaSchedulerColumns";
import { PGA_MODULE, PGA_ALERT_DAYS, DELETE_LABEL } from "./constants/pgaSchedulerConstants";
import { AddDataImportModal } from "../schedulers/AddDataImportModal";
import { DeleteSchedulerDialog } from "../schedulers/DeleteSchedulerDialog";
import { DetailsModal } from "../schedulers/DetailsModal";
import {
  mapDataImportToRow,
  moduleLabelFromValue,
  normalizeKey,
  toolLabelFromValue,
} from "../schedulers/schedulerMappers";
import { SchedulerToolbar } from "../schedulers/SchedulerToolbar";
import {
  type AddDataImportPayload,
  type SchedulerRow,
  type SelectItem,
} from "../schedulers/schedulerTypes";

type SortDir = "asc" | "desc";

/** This page schedules a single module, so its Module field is fixed. */
const PGA_MOD: SelectItem = {
  value: "privilege_governance_assurance",
  label: "Privilege Governance Assurance",
};

/** The PAG endpoint keeps an `alertDays` field that this page does not expose. */
const PGA_ALERT = 0;

const DELETE_LBL = "Privilege Governance Assurance";

export function PgaScheduler() {
  const username = useAuthStore((s) => s.user?.username ?? "");

  const canCreate = useRBACPermission("scheduler", "create");
  const canUpdate = useRBACPermission("scheduler", "edit");
  const canDelete = useRBACPermission("scheduler", "delete");
  const canEnableDisable = useRBACPermission("scheduler", "update");

  const [rows, setRows] = useState<SchedulerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(MAX_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (!value.trim()) {
      setAppliedSearch("");
      setPage(0);
    }
  };

  const handleSearchSubmit = (value: string) => {
    setAppliedSearch(value.trim().toLowerCase());
    setPage(0);
  };

  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [detailsRow, setDetailsRow] = useState<SchedulerRow | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SchedulerRow | null>(null);
  const [tools, setTools] = useState<string[]>(["AWS", "Active Directory", "Linux"]);

  useEffect(() => {
    fetchSourceSystemTools().then((res) => {
      if (res && res.length > 0) {
        setTools(res);
      }
    });
  }, []);

  const toolOptions: SelectItem[] = useMemo(
    () =>
      tools.map((t) => ({
        value: normalizeKey(t),
        label: t,
      })),
    [tools],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchPagSchedulers();
      setRows((res ?? []).map(mapDataImportToRow));
    } catch (error) {
      showToast("error", getErrorMessage(error, "Failed to load schedulers"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const query = appliedSearch;
    const matched = query
      ? rows.filter((row) =>
          [row.moduleName, row.tool, row.importFrequency, row.importOn, row.time]
            .join(" ")
            .toLowerCase()
            .includes(query),
        )
      : rows;
    if (!sortKey) return matched;
    return [...matched].sort((a, b) => {
      const av = String(a[sortKey as keyof SchedulerRow] ?? "");
      const bv = String(b[sortKey as keyof SchedulerRow] ?? "");
      return sortDir === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
    });
  }, [rows, appliedSearch, sortKey, sortDir]);

  const pageRows = useMemo(
    () => filtered.slice(page * perPage, page * perPage + perPage),
    [filtered, page, perPage],
  );

  const handleSort = useCallback((key: string) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  }, [sortKey, sortDir]);

  const closeForm = useCallback(() => {
    setAddOpen(false);
    setEditingId(null);
  }, []);

  const handleSave = async (payload: AddDataImportPayload) => {
    const isEdit = editingId != null;
    const existing = isEdit ? rows.find((r) => r.id === editingId) : undefined;
    const matched = toolOptions.find(
      (o) => o.value === payload.tool || o.label.toLowerCase() === payload.tool.toLowerCase(),
    );
    const resolvedTool = matched ? matched.label : toolLabelFromValue(payload.tool);
    try {
      await savePagScheduler({
        ...(isEdit ? { id: editingId, updateBy: username } : { createBy: username }),
        moduleName: moduleLabelFromValue(payload.module),
        tool: resolvedTool,
        time: payload.importAt,
        alertDays: PGA_ALERT,
        importOn: payload.importOn || payload.importFrequency,
        importFrequency: payload.importFrequency,
        status: existing?.status ?? true,
      });
      if (!isEdit) setPage(0);
      await load();
      showToast("success", isEdit ? "Scheduler updated successfully" : "Scheduler added successfully");
      closeForm();
    } catch (error) {
      showToast("error", getErrorMessage(error, "Failed to save scheduler"));
    }
  };

  const handleStatus = async (row: SchedulerRow, next: boolean) => {
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
    try {
      await savePagScheduler({
        id: row.id,
        moduleName: row.moduleName,
        tool: row.tool,
        time: row.time,
        alertDays: PGA_ALERT,
        importOn: row.importOn,
        importFrequency: row.importFrequency,
        status: next,
        updateBy: username,
      });
      showToast("success", next ? "Scheduler enabled" : "Scheduler disabled");
    } catch (error) {
      setRows(
        prev => prev.map((r) => (r.id === row.id ? { ...r, status: row.status } : r)),
      );
      showToast("error", getErrorMessage(error, "Failed to update scheduler status"));
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deletePagScheduler(pendingDelete.id);
      await load();
      showToast("success", "Scheduler deleted successfully");
      setPendingDelete(null);
    } catch (error) {
      showToast("error", getErrorMessage(error, "Failed to delete scheduler"));
    }
  };

  const columns: Column<SchedulerRow>[] = useMemo(
    () =>
      buildPgaSchedulerColumns({
        page,
        pageSize: perPage,
        canUpdate,
        canDelete,
        canEnableDisable,
        onToggleStatus: handleStatus,
        onViewDetails: setDetailsRow,
        onEdit: (row) => {
          setEditingId(row.id);
          setAddOpen(true);
        },
        onDelete: setPendingDelete,
      }),
    [page, perPage, canUpdate, canDelete, canEnableDisable],
  );

  return (
    <div className="space-y-4">
      <SchedulerToolbar
        search={search}
        onSearchChange={handleSearchChange}
        onSubmit={handleSearchSubmit}
        onAdd={
          canCreate
            ? () => {
              setEditingId(null);
              setAddOpen(true);
            }
            : undefined
        }
      />

      <DataTable
        columns={columns}
        data={pageRows}
        loading={loading}
        rowKey={(r) => r.id}
        emptyText="No Data Available"
        page={page}
        pageSize={perPage}
        totalItems={filtered.length}
        itemLabel="schedulers"
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPerPage(n);
          setPage(0);
        }}
        sortKey={sortKey ?? undefined}
        sortDir={sortDir}
        onSort={handleSort}
      />

      {addOpen && (
        <AddDataImportModal
          open={addOpen}
          mode={editingId != null ? "edit" : "add"}
          initial={{
            module: normalizeKey(editingId != null ? rows.find((r) => r.id === editingId)?.moduleName ?? "" : ""),
            tool: normalizeKey(editingId != null ? rows.find((r) => r.id === editingId)?.tool ?? "" : ""),
            importFrequency: editingId != null ? rows.find((r) => r.id === editingId)?.importFrequency ?? "" : "",
            importOn: editingId != null ? rows.find((r) => r.id === editingId)?.importOn ?? "" : "",
            importAt: editingId != null ? rows.find((r) => r.id === editingId)?.time ?? "" : "",
          }}
          fixedModule={PGA_MOD}
          toolOptions={toolOptions}
          onClose={closeForm}
          onSave={handleSave}
        />
      )}

      <DetailsModal
        open={!!detailsRow}
        moduleName={detailsRow?.moduleName ?? ""}
        configuredBy={detailsRow?.createdBy ?? ""}
        configuredOn={detailsRow?.createdDate ?? 0}
        updatedBy={detailsRow?.updatedBy ?? ""}
        updatedOn={detailsRow?.updatedDate ?? 0}
        onClose={() => setDetailsRow(null)}
      />

      {pendingDelete && (
        <DeleteSchedulerDialog
          label={DELETE_LABEL}
          onCancel={() => setPendingDelete(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}
